import {
	GlobalRoleEnum,
	TenantRoleEnum,
} from "../types/user.type";

export const setActiveTenant = (tenantId: string) => {
	sessionStorage.setItem("activeTenant", tenantId);
};

export const getActiveTenant = () => {
	// Each tab can have a different tenant. Keep the old local value only as a
	// one-time preference for tabs that have not selected a tenant yet.
	return (
		sessionStorage.getItem("activeTenant") ??
		localStorage.getItem("activeTenant")
	);
};

export const clearActiveTenant = () => {
	sessionStorage.removeItem("activeTenant");
};

const NON_TENANT_ROUTE_SEGMENTS = new Set([
	"",
	"add-dataset",
	"agent",
	"analysis",
	"create-password",
	"dataset-formulae",
	"dataset-paul-sync",
	"ledger",
	"library",
	"lithe",
	"lithe-library",
	"login",
	"multi-factor-verification",
	"sanitize",
	"sentry-test",
	"users",
]);

export const getTenantIdFromPathname = (
	pathname = window.location.pathname
) => {
	const firstSegment = pathname
		.split("/")
		.filter(Boolean)[0];

	if (
		!firstSegment ||
		NON_TENANT_ROUTE_SEGMENTS.has(firstSegment)
	) {
		return null;
	}

	return decodeURIComponent(firstSegment);
};

export const stripTenantFromPathname = (
	pathname = window.location.pathname
) => {
	const tenantIdFromPath =
		getTenantIdFromPathname(pathname);

	if (!tenantIdFromPath) {
		return pathname;
	}

	const segments = pathname.split("/");
	const strippedPathname = `/${segments.slice(2).join("/")}`;

	return strippedPathname === "/" ? "/" : strippedPathname;
};

export const toTenantPath = (
	path: string,
	tenantId = getActiveTenant()
) => {
	if (!tenantId) {
		return path;
	}

	if (/^https?:\/\//.test(path)) {
		const url = new URL(path);
		url.pathname = toTenantPath(
			`${url.pathname}${url.search}${url.hash}`,
			tenantId
		).split(/[?#]/)[0];
		return url.toString();
	}

	const hashIndex = path.indexOf("#");
	const pathWithoutHash =
		hashIndex === -1 ? path : path.slice(0, hashIndex);
	const hash =
		hashIndex === -1 ? "" : path.slice(hashIndex);
	const queryIndex = pathWithoutHash.indexOf("?");
	const pathname =
		queryIndex === -1
			? pathWithoutHash
			: pathWithoutHash.slice(0, queryIndex);
	const search =
		queryIndex === -1
			? ""
			: pathWithoutHash.slice(queryIndex);
	const normalizedPathname = pathname.startsWith("/")
		? pathname
		: `/${pathname}`;
	const pathnameWithoutTenant = stripTenantFromPathname(
		normalizedPathname
	);
	const tenantPrefix = `/${encodeURIComponent(tenantId)}`;

	return `${tenantPrefix}${
		pathnameWithoutTenant === "/"
			? ""
			: pathnameWithoutTenant
	}${search}${hash}`;
};

export const toCurrentTenantPath = (
	path: string,
	pathname = window.location.pathname
) =>
	toTenantPath(
		path,
		getTenantIdFromPathname(pathname) ?? getActiveTenant()
	);

const getTenantIdsFromClaim = (
	tenantsClaim: unknown
): Array<string> => {
	if (!tenantsClaim) {
		return [];
	}

	try {
		const tenants =
			typeof tenantsClaim === "string"
				? JSON.parse(tenantsClaim)
				: tenantsClaim;

		if (typeof tenants !== "object" || tenants === null) {
			return [];
		}

		return Object.keys(tenants);
	} catch {
		return [];
	}
};

export const getPreferredActiveTenant = (
	tenantIds: Array<string>,
	currentTenant: string | null = getActiveTenant()
) => {
	if (tenantIds.length === 0) {
		return null;
	}

	if (currentTenant && tenantIds.includes(currentTenant)) {
		return currentTenant;
	}

	return tenantIds[0];
};

export const syncActiveTenantFromAuthClaims = (
	decodedToken: Record<string, unknown>
) => {
	const currentTenant = getActiveTenant();
	if (currentTenant) {
		return currentTenant;
	}

	const preferredTenant = getPreferredActiveTenant(
		getTenantIdsFromClaim(decodedToken.tenants),
		currentTenant
	);

	if (preferredTenant) {
		setActiveTenant(preferredTenant);
	}

	return preferredTenant;
};

export const getRandomColor = () => {
	const letters = "0123456789ABCDEF";
	let color = "#";
	for (let i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	// Check if the color is light, if so, make it dark
	const luminance =
		(0.299 * parseInt(color.slice(1, 3), 16) +
			0.587 * parseInt(color.slice(3, 5), 16) +
			0.114 * parseInt(color.slice(5, 7), 16)) /
		255;
	if (luminance > 0.6) {
		color = "#7f56d9";
	}
	return color;
};

export const hashUserIdToColor = (userId: string) => {
	const colors = [
		"#054F31",
		"3039855",
		"#DC6803",
		"#B54708",
		"#B42318",
		"#447546",
		"#3B7C07",
		"#087443",
		"#107569",
		"#088AB2",
		"#026AA2",
		"#0B4A6F",
		"#6927DA",
		"#4A1FB8",
		"#9F1AB1",
		"#DD2590",
		"#C01048",
		"#F63D68",
		"#F63D68",
		"#FF4405",
		"#BC1B06",
		"#EF6820",
		"#E04F16",
		"#EAAA08",
		"#A15C07",
		"#713B12",
	];

	// Generate a hash code from the userId
	let hashCode = 0;
	for (let i = 0; i < userId.length; i++) {
		hashCode =
			(hashCode << 5) - hashCode + userId.charCodeAt(i);
		hashCode |= 0; // Convert to 32-bit integer
	}

	// Get the index of the color based on the hash code
	const colorIndex = Math.abs(hashCode) % colors.length;

	return colors[colorIndex];
};
export const getNameInitials = (username: string) => {
	const names = username.trim().split(" ");
	if (names.length === 1) {
		return names[0].charAt(0).toUpperCase();
	}
	return (
		names[0].charAt(0) + names[names.length - 1].charAt(0)
	).toUpperCase();
};

export const getTenantRoleLabel = (role: string) => {
	switch (role) {
		case TenantRoleEnum.TENANT_ADMIN:
			return "Admin";
		case TenantRoleEnum.TENANT_USER:
			return "User";
		case TenantRoleEnum.TENANT_READ_ONLY_USER:
			return "Read Only";
		default:
			return "--";
	}
};

type TTenantCollection =
	| Record<string, any>
	| Array<Record<string, any>>
	| null
	| undefined;

export const buildTenantNameMap = (
	...tenantCollections: Array<TTenantCollection>
) => {
	const tenantNameById: Record<string, string> = {};

	tenantCollections.forEach((tenantCollection) => {
		if (!tenantCollection) {
			return;
		}

		const tenants = Array.isArray(tenantCollection)
			? tenantCollection.map((tenant) => [
					tenant?.id ?? tenant?.tenant_id,
					tenant,
				])
			: Object.entries(tenantCollection);

		tenants.forEach(([tenantIdFromKey, tenant]) => {
			const tenantId = String(
				tenant?.id ??
					tenant?.tenant_id ??
					tenantIdFromKey ??
					""
			);
			const tenantName =
				tenant?.tenant_name ?? tenant?.name;

			if (tenantId && tenantName) {
				tenantNameById[tenantId] = tenantName;
			}
		});
	});

	return tenantNameById;
};

export const getTenantDisplayName = (
	tenantId: string,
	tenantNameById: Record<string, string>
) => {
	return tenantNameById[tenantId] ?? tenantId;
};

export const isReadOnlyForTenant = (
	user: any,
	tenantId: string | null | undefined
) => {
	if (!user || !tenantId) {
		return false;
	}

	return (
		user.global_role === GlobalRoleEnum.SCOPED_USER &&
		user.tenants?.[tenantId]?.role ===
			TenantRoleEnum.TENANT_READ_ONLY_USER
	);
};
